import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '@/components';
import { VBMCardTitle, VBMEditButton } from '..';
import useResponsive from '@/hooks/useResponsive';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import type {
  BookingAdditionalService,
  BookingSession,
} from '@/features/bookings-management/types';
import { fCurrency } from '@/utils/formatNumber';

type AdditionalServiceRow = BookingAdditionalService & {
  clientKey: string;
  priceInput: string;
};

type RowErrors = {
  name?: string;
  price?: string;
};

type VBMAdditionalServicesProps = {
  editable?: boolean;
  bookingSession?: BookingSession;
  sessionView?: boolean;
};

const createRow = (
  service: BookingAdditionalService | undefined,
  fallbackKey: string
): AdditionalServiceRow => ({
  id: service?.id,
  name: service?.name ?? '',
  price: Number(service?.price ?? 0),
  priceInput:
    service?.price === undefined || service?.price === null
      ? ''
      : String(service.price),
  clientKey: service?.id != null ? `service-${service.id}` : fallbackKey,
});

const normalizeServices = (
  services: BookingAdditionalService[] | undefined,
  nextKey: () => string
) => (services ?? []).map((service) => createRow(service, nextKey()));

const getServiceTotal = (services: BookingAdditionalService[] | undefined) =>
  (services ?? []).reduce((total, service) => {
    const price = Number(service.price);
    return total + (Number.isFinite(price) ? price : 0);
  }, 0);

export default function VBMAdditionalServices({
  editable = false,
  bookingSession,
  sessionView = false,
}: VBMAdditionalServicesProps) {
  const downMd = useResponsive('down', 'md');
  const keyCounter = useRef(0);
  const getNextKey = () => {
    keyCounter.current += 1;
    return `new-service-${keyCounter.current}`;
  };

  const [isEdit, setIsEdit] = useState(false);
  const [rows, setRows] = useState<AdditionalServiceRow[]>(() =>
    normalizeServices(bookingSession?.additional_services, getNextKey)
  );
  const [errors, setErrors] = useState<Record<string, RowErrors>>({});

  useEffect(() => {
    if (!isEdit) {
      setRows(
        normalizeServices(bookingSession?.additional_services, getNextKey)
      );
      setErrors({});
    }
  }, [bookingSession?.additional_services, isEdit]);

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => {
      setIsEdit(false);
      setErrors({});
    },
    successText: 'Extra services updated',
  });

  const services = bookingSession?.additional_services ?? [];
  const hasServices = services.length > 0;
  const canEdit = editable && sessionView && !!bookingSession;
  const iconSize = downMd ? 20 : 30;
  const total = useMemo(() => getServiceTotal(services), [services]);

  const validateRows = () => {
    const nextErrors: Record<string, RowErrors> = {};

    rows.forEach((row) => {
      const rowErrors: RowErrors = {};
      const price = Number(row.priceInput);

      if (!row.name.trim()) {
        rowErrors.name = 'Enter a service name';
      }

      if (!Number.isFinite(price) || price <= 0) {
        rowErrors.price = 'Enter a price greater than 0';
      }

      if (rowErrors.name || rowErrors.price) {
        nextErrors[row.clientKey] = rowErrors;
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleStartEdit = () => {
    const initialRows = normalizeServices(
      bookingSession?.additional_services,
      getNextKey
    );
    setRows(
      initialRows.length ? initialRows : [createRow(undefined, getNextKey())]
    );
    setErrors({});
    setIsEdit(true);
  };

  const handleCancel = () => {
    setRows(normalizeServices(bookingSession?.additional_services, getNextKey));
    setErrors({});
    setIsEdit(false);
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, createRow(undefined, getNextKey())]);
  };

  const handleRemoveRow = (clientKey: string) => {
    setRows((prevRows) => prevRows.filter((row) => row.clientKey !== clientKey));
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      delete nextErrors[clientKey];
      return nextErrors;
    });
  };

  const handleChangeRow = (
    clientKey: string,
    field: 'name' | 'priceInput',
    value: string
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.clientKey === clientKey
          ? {
              ...row,
              [field]: value,
              ...(field === 'priceInput' ? { price: Number(value) } : {}),
            }
          : row
      )
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [clientKey]: {
        ...prevErrors[clientKey],
        [field === 'name' ? 'name' : 'price']: undefined,
      },
    }));
  };

  const handleSave = () => {
    if (!bookingSession || !validateRows()) return;

    sessionUpdateMutation.mutate({
      bookingSessionId: bookingSession.id,
      bookingSessionDto: {
        additional_services: rows.map((row) => ({
          ...(row.id != null ? { id: row.id } : {}),
          name: row.name.trim(),
          price: Number(row.priceInput),
        })),
      },
    });
  };

  return (
    <Stack sx={{ position: 'relative', py: 0.5 }} spacing={1}>
      {!isEdit && canEdit && <VBMEditButton onClickButton={handleStartEdit} />}
      <VBMCardTitle title="Extra Services" />

      {isEdit ? (
        <Stack spacing={1.5}>
          {rows.map((row, index) => (
            <Stack
              key={row.clientKey}
              direction={downMd ? 'column' : 'row'}
              spacing={1}
              alignItems={downMd ? 'stretch' : 'flex-start'}
            >
              <TextField
                label="Service name"
                size="small"
                value={row.name}
                onChange={(event) =>
                  handleChangeRow(row.clientKey, 'name', event.target.value)
                }
                error={Boolean(errors[row.clientKey]?.name)}
                helperText={errors[row.clientKey]?.name}
                fullWidth
              />
              <TextField
                label="Price"
                size="small"
                type="number"
                value={row.priceInput}
                onChange={(event) =>
                  handleChangeRow(
                    row.clientKey,
                    'priceInput',
                    event.target.value
                  )
                }
                inputProps={{ min: 0, step: '0.01' }}
                error={Boolean(errors[row.clientKey]?.price)}
                helperText={errors[row.clientKey]?.price}
                sx={{ width: downMd ? 1 : 140 }}
              />
              <IconButton
                aria-label={`Remove extra service ${index + 1}`}
                onClick={() => handleRemoveRow(row.clientKey)}
                size="small"
                sx={{
                  mt: downMd ? 0 : 0.5,
                  alignSelf: downMd ? 'flex-end' : 'auto',
                }}
              >
                <Iconify icon="eva:trash-2-outline" width={18} />
              </IconButton>
            </Stack>
          ))}

          <Button
            variant="text"
            size="small"
            onClick={handleAddRow}
            startIcon={<Iconify icon="eva:plus-outline" width={18} />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            Add service
          </Button>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleCancel}
              disabled={sessionUpdateMutation.isLoading}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={handleSave}
              loading={sessionUpdateMutation.isLoading}
            >
              {downMd ? 'Save' : 'Save changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={0.75} sx={{ pl: 1, pt: 0.5 }}>
          {hasServices ? (
            <>
              {services.map((service, index) => (
                <Stack
                  key={service.id ?? `${service.name}-${index}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 35,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify icon="solar:tag-price-outline" width={iconSize} />
                    </Box>
                    <Typography fontSize={downMd ? 12 : 16}>
                      {service.name}
                    </Typography>
                  </Stack>
                  <Typography
                    fontSize={downMd ? 12 : 16}
                    fontWeight={600}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {fCurrency(service.price)}
                  </Typography>
                </Stack>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ pl: 5 }}
              >
                <Typography fontSize={downMd ? 12 : 14} color="text.secondary">
                  Total
                </Typography>
                <Typography fontSize={downMd ? 12 : 14} fontWeight={600}>
                  {fCurrency(total)}
                </Typography>
              </Stack>
            </>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon="solar:tag-price-outline" width={iconSize} />
              </Box>
              <Typography fontSize={downMd ? 12 : 16}>
                No extra services
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
